import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { brandingService } from '../services/brandingService'
import { supabase } from '../lib/supabase'
import { 
  BrandingProfile, 
  CreateBrandingProfileData, 
  brandingColorPalettes,
  documentFonts 
} from '../types/branding'
import {
  SwatchIcon as Palette,
  ArrowUpTrayIcon as Upload,
  EyeIcon as Eye,
  FolderIcon as Save,
  DocumentTextIcon as FileText,
  PaintBrushIcon as Paintbrush,
  PhotoIcon as Image,
  DocumentIcon as Type,
  PhoneIcon as Phone,
  EnvelopeIcon as Mail,
  GlobeAltIcon as Globe,
  MapPinIcon as MapPin,
  HashtagIcon as Hash,
  BuildingOfficeIcon as Building
} from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import AppHeader from '../components/AppHeader'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import ProfileDropdown from '../components/ProfileDropdown'

export default function BrandingPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const [brandingProfile, setBrandingProfile] = useState<BrandingProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'business' | 'visual' | 'documents' | 'preview'>('business')
  const [isUploading, setIsUploading] = useState(false)
  
  // Form state
  const [formData, setFormData] = useState<CreateBrandingProfileData>({
    businessName: '',
    tagline: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    businessWebsite: '',
    vatNumber: '',
    registrationNumber: '',
    logoPosition: 'left',
    primaryColor: '#2563eb',
    secondaryColor: '#64748b',
    accentColor: '#0ea5e9',
    documentFont: 'modern',
    headerStyle: 'professional',
    footerText: '',
    watermarkText: '',
    showWatermark: false,
    showBusinessAddress: true,
    showBusinessPhone: true,
    showBusinessEmail: true,
    showBusinessWebsite: true,
    showVatNumber: false,
    showRegistrationNumber: false
  })

  // Define tabs array
  const tabs = [
    { id: 'business' as const, name: 'Business Info', icon: Building },
    { id: 'visual' as const, name: 'Visual Identity', icon: Palette },
    { id: 'documents' as const, name: 'Document Settings', icon: FileText },
    { id: 'preview' as const, name: 'Preview', icon: Eye }
  ]

  useEffect(() => {
    if (user) {
      loadBrandingProfile()
    }
  }, [user])

  const loadBrandingProfile = async () => {
    try {
      setIsLoading(true)
      const profile = await brandingService.getBrandingProfile(user!.id)
      if (profile) {
        setBrandingProfile(profile)
        setFormData({
          businessName: profile.businessName,
          tagline: profile.tagline || '',
          businessAddress: profile.businessAddress || '',
          businessPhone: profile.businessPhone || '',
          businessEmail: profile.businessEmail || '',
          businessWebsite: profile.businessWebsite || '',
          vatNumber: profile.vatNumber || '',
          registrationNumber: profile.registrationNumber || '',
          logoPosition: profile.logoPosition,
          primaryColor: profile.primaryColor,
          secondaryColor: profile.secondaryColor,
          accentColor: profile.accentColor,
          documentFont: profile.documentFont,
          headerStyle: profile.headerStyle,
          footerText: profile.footerText || '',
          watermarkText: profile.watermarkText || '',
          showWatermark: profile.showWatermark,
          showBusinessAddress: profile.showBusinessAddress,
          showBusinessPhone: profile.showBusinessPhone,
          showBusinessEmail: profile.showBusinessEmail,
          showBusinessWebsite: profile.showBusinessWebsite,
          showVatNumber: profile.showVatNumber,
          showRegistrationNumber: profile.showRegistrationNumber
        })
      }
    } catch (error) {
      console.error('Error loading branding profile:', error)
      showError('Error', 'Failed to load branding profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!user) return
    
    setIsSaving(true)
    try {
      const result = brandingProfile 
        ? await brandingService.updateBrandingProfile(user.id, formData)
        : await brandingService.createBrandingProfile(user.id, formData)
      
      if (result.success && result.profile) {
        setBrandingProfile(result.profile)
        setFormData(result.profile)
        showSuccess('Success', 'Branding profile saved successfully')
      } else {
        showError('Error', result.error || 'Failed to save branding profile')
      }
    } catch (error) {
      console.error('Error saving branding profile:', error)
      showError('Error', 'Failed to save branding profile')
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user || !supabase) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Error', 'Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Error', 'File size must be less than 5MB')
      return
    }

    setIsUploading(true)
    try {
      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/logo-${Date.now()}.${fileExt}`
      
      const { error } = await supabase.storage
        .from('branding-assets')
        .upload(fileName, file)

      if (error) throw error

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('branding-assets')
        .getPublicUrl(fileName)

      // Update form data with logo URL
      const updatedFormData = { ...formData, logoUrl: urlData.publicUrl }
      
      // Update profile
      const updatedProfile = brandingProfile 
        ? await brandingService.updateBrandingProfile(user.id, updatedFormData)
        : await brandingService.createBrandingProfile(user.id, updatedFormData)

      if (updatedProfile.success && updatedProfile.profile) {
        setBrandingProfile(updatedProfile.profile)
        setFormData(updatedFormData)
        showSuccess('Success', 'Logo uploaded successfully')
      }
    } catch (error) {
      console.error('Error uploading logo:', error)
      showError('Error', 'Failed to upload logo')
    } finally {
      setIsUploading(false)
    }
  }

  const applyColorPalette = (palette: any) => {
    setFormData(prev => ({
      ...prev,
      primaryColor: palette.primary,
      secondaryColor: palette.secondary,
      accentColor: palette.accent
    }))
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">Brand Management</h1>
          <p className="text-gray-400">Customize your business branding for professional documents and communications</p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-xl border border-white/10 p-2 mb-8 inline-flex"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <div className="flex items-center space-x-2">
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </div>
            </button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-2xl p-8 border border-white/10"
        >
          {/* Business Information Tab */}
          {activeTab === 'business' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-6">Business Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="Your Photography Business"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Tagline
                  </label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="Capturing life's precious moments"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    value={formData.businessEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessEmail: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="hello@yourbusiness.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Business Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.businessPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessPhone: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.businessWebsite}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessWebsite: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="https://yourbusiness.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Business Address
                </label>
                <textarea
                  value={formData.businessAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessAddress: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
                  placeholder="123 Photography Street, Studio City, CA 90210"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    VAT Number
                  </label>
                  <input
                    type="text"
                    value={formData.vatNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="VAT123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Registration Number
                  </label>
                  <input
                    type="text"
                    value={formData.registrationNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, registrationNumber: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="REG123456"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Visual Identity Tab */}
          {activeTab === 'visual' && (
            <div className="space-y-8">
              {/* Logo Upload */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logo
                </h3>
                
                <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center glass-mockup">
                  {brandingProfile?.logoUrl ? (
                    <div className="space-y-4">
                      <img 
                        src={brandingProfile.logoUrl} 
                        alt="Logo" 
                        className="mx-auto h-20 object-contain"
                      />
                      <p className="text-sm text-gray-400">Current logo</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="text-gray-400">No logo uploaded</p>
                    </div>
                  )}
                  
                  <label className="mt-4 inline-flex items-center px-4 py-2 border border-white/20 rounded-lg shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 cursor-pointer transition-all">
                    <Upload className="w-4 h-4 mr-2" />
                    {isUploading ? 'Uploading...' : 'Upload Logo'}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Logo Position</label>
                  <div className="flex space-x-4">
                    {['left', 'center', 'right'].map((position) => (
                      <label key={position} className="flex items-center">
                        <input
                          type="radio"
                          name="logoPosition"
                          value={position}
                          checked={formData.logoPosition === position}
                          onChange={(e) => setFormData(prev => ({ ...prev, logoPosition: e.target.value as any }))}
                          className="h-4 w-4 text-blue-500 focus:ring-blue-500/50 border-white/20 bg-white/10"
                        />
                        <span className="ml-2 text-sm text-gray-300 capitalize">{position}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Color Palette */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Paintbrush className="w-5 h-5" />
                  Color Palette
                </h3>
                
                {/* Preset Palettes */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Preset Palettes</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries(brandingColorPalettes).map(([key, palette]) => (
                      <button
                        key={key}
                        onClick={() => applyColorPalette(palette)}
                        className="p-4 glass-mockup border border-white/10 rounded-lg hover:border-white/20 transition-all"
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.primary }}></div>
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.secondary }}></div>
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.accent }}></div>
                        </div>
                        <p className="text-sm text-gray-300 text-left">{palette.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Primary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-12 h-10 border border-white/10 rounded-lg cursor-pointer bg-white/10"
                      />
                      <input
                        type="text"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="flex-1 px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Secondary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.secondaryColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="w-12 h-10 border border-white/10 rounded-lg cursor-pointer bg-white/10"
                      />
                      <input
                        type="text"
                        value={formData.secondaryColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="flex-1 px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Accent Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.accentColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="w-12 h-10 border border-white/10 rounded-lg cursor-pointer bg-white/10"
                      />
                      <input
                        type="text"
                        value={formData.accentColor}
                        onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                        className="flex-1 px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Document Settings Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-8">
              {/* Typography */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Typography
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Document Font</label>
                    <select
                      value={formData.documentFont}
                      onChange={(e) => setFormData(prev => ({ ...prev, documentFont: e.target.value as any }))}
                      className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    >
                      {Object.entries(documentFonts).map(([key, font]) => (
                        <option key={key} value={key} className="bg-gray-900 text-white">{font.name} - {font.description}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Header Style</label>
                    <select
                      value={formData.headerStyle}
                      onChange={(e) => setFormData(prev => ({ ...prev, headerStyle: e.target.value as any }))}
                      className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    >
                      <option value="minimal" className="bg-gray-900 text-white">Minimal</option>
                      <option value="professional" className="bg-gray-900 text-white">Professional</option>
                      <option value="creative" className="bg-gray-900 text-white">Creative</option>
                      <option value="corporate" className="bg-gray-900 text-white">Corporate</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Watermark Settings */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4">Watermark</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.showWatermark}
                      onChange={(e) => setFormData(prev => ({ ...prev, showWatermark: e.target.checked }))}
                      className="h-4 w-4 text-blue-500 focus:ring-blue-500/50 border-white/20 rounded bg-white/10"
                    />
                    <span className="ml-2 text-sm text-gray-300">Show watermark on documents</span>
                  </label>

                  {formData.showWatermark && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Watermark Text</label>
                      <input
                        type="text"
                        value={formData.watermarkText}
                        onChange={(e) => setFormData(prev => ({ ...prev, watermarkText: e.target.value }))}
                        className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                        placeholder="CONFIDENTIAL"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Text */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Footer Text</label>
                <textarea
                  value={formData.footerText}
                  onChange={(e) => setFormData(prev => ({ ...prev, footerText: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
                  placeholder="Thank you for your business!"
                />
              </div>

              {/* Contact Information Display */}
              <div>
                <h3 className="text-lg font-medium text-white mb-4">Contact Information Display</h3>
                <p className="text-sm text-gray-400 mb-4">Choose which contact information to show on documents</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'showBusinessAddress', label: 'Business Address', icon: MapPin },
                    { key: 'showBusinessPhone', label: 'Business Phone', icon: Phone },
                    { key: 'showBusinessEmail', label: 'Business Email', icon: Mail },
                    { key: 'showBusinessWebsite', label: 'Website', icon: Globe },
                    { key: 'showVatNumber', label: 'VAT Number', icon: Hash },
                    { key: 'showRegistrationNumber', label: 'Registration Number', icon: Building }
                  ].map(({ key, label, icon: Icon }) => (
                    <label key={key} className="flex items-center p-3 glass-mockup border border-white/10 rounded-lg">
                      <input
                        type="checkbox"
                        checked={(formData as any)[key] as boolean}
                        onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="h-4 w-4 text-blue-500 focus:ring-blue-500/50 border-white/20 rounded bg-white/10"
                      />
                      <Icon className="w-4 h-4 ml-3 mr-2 text-gray-400" />
                      <span className="text-sm text-gray-300">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-white mb-4">Document Preview</h3>
              
              {/* Preview Document */}
              <div className="glass-mockup border border-white/10 rounded-lg p-8 max-w-2xl mx-auto">
                <div 
                  className="space-y-6"
                  style={{ 
                    fontFamily: documentFonts[formData.documentFont as keyof typeof documentFonts]?.fontFamily || 'system-ui, -apple-system, sans-serif',
                    color: formData.primaryColor 
                  }}
                >
                  {/* Header */}
                  <div className={`flex items-center ${formData.logoPosition === 'center' ? 'justify-center' : formData.logoPosition === 'right' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`${formData.logoPosition === 'center' ? 'text-center' : ''}`}>
                      <h1 className="text-2xl font-bold">{formData.businessName || 'Your Business Name'}</h1>
                      {formData.tagline && (
                        <p className="text-sm mt-1" style={{ color: formData.secondaryColor }}>{formData.tagline}</p>
                      )}
                    </div>
                  </div>

                  {/* Sample Content */}
                  <div className="border-t border-white/20 pt-6">
                    <h2 className="text-lg font-semibold mb-4" style={{ color: formData.accentColor }}>
                      Photography Services Invoice
                    </h2>
                    
                    <div className="space-y-2 text-sm">
                      <p><strong>Invoice #:</strong> INV-2024-001</p>
                      <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                      <p><strong>Due Date:</strong> {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                    </div>

                    <div className="mt-6 p-4 rounded" style={{ backgroundColor: `${formData.accentColor}10` }}>
                      <div className="flex justify-between">
                        <span>Wedding Photography Package</span>
                        <span className="font-semibold">$2,500.00</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-white/20 pt-6 text-xs space-y-2" style={{ color: formData.secondaryColor }}>
                    {formData.showBusinessAddress && formData.businessAddress && (
                      <p>{formData.businessAddress}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4">
                      {formData.showBusinessEmail && formData.businessEmail && (
                        <span>{formData.businessEmail}</span>
                      )}
                      {formData.showBusinessPhone && formData.businessPhone && (
                        <span>{formData.businessPhone}</span>
                      )}
                      {formData.showBusinessWebsite && formData.businessWebsite && (
                        <span>{formData.businessWebsite}</span>
                      )}
                    </div>

                    {(formData.showVatNumber && formData.vatNumber) || (formData.showRegistrationNumber && formData.registrationNumber) ? (
                      <div className="flex gap-4">
                        {formData.showVatNumber && formData.vatNumber && (
                          <span>VAT: {formData.vatNumber}</span>
                        )}
                        {formData.showRegistrationNumber && formData.registrationNumber && (
                          <span>Reg: {formData.registrationNumber}</span>
                        )}
                      </div>
                    ) : null}

                    {formData.footerText && (
                      <p className="mt-4 italic">{formData.footerText}</p>
                    )}
                  </div>

                  {/* Watermark */}
                  {formData.showWatermark && formData.watermarkText && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ 
                        transform: 'rotate(-45deg)',
                        opacity: 0.1,
                        fontSize: '3rem',
                        fontWeight: 'bold',
                        color: formData.primaryColor
                      }}
                    >
                      {formData.watermarkText}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving || !formData.businessName}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Save className="w-5 h-5 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  )
} 